import { Controller } from '@hotwired/stimulus';

/**
 * DOB Field Controller
 *
 * Automatically formats date input to MM/DD/YYYY pattern as user types.
 * Handles:
 * - Auto-insertion of slashes at positions 2 and 5
 * - Proper backspace/delete behavior - can delete slashes
 * - Paste support with automatic formatting
 * - Max length enforcement (10 characters)
 */
export default class extends Controller {
  static targets = ['input'];

  connect() {
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('input', this.formatDate.bind(this));
      this.inputTarget.addEventListener('keydown', this.handleKeydown.bind(this));
    }
  }

  disconnect() {
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener('input', this.formatDate.bind(this));
      this.inputTarget.removeEventListener('keydown', this.handleKeydown.bind(this));
    }
  }

  handleKeydown(event) {
    const input = event.target;
    const cursorPosition = input.selectionStart;
    const value = input.value;

    // Handle backspace key
    if (event.key === 'Backspace') {
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        event.preventDefault();

        // Remove the slash and reformat
        const newValue = value.substring(0, cursorPosition - 1) + value.substring(cursorPosition);
        input.value = newValue;

        // Trigger input event to reformat
        input.dispatchEvent(new Event('input', { bubbles: true }));

        // Set cursor position after the reformatting
        setTimeout(() => {
          const numericOnly = newValue.replace(/\D/g, '');
          let newCursorPos = cursorPosition - 1;

          // Adjust cursor position based on how many slashes are before it
          if (numericOnly.length >= 2 && newCursorPos > 2) {
            newCursorPos = Math.min(newCursorPos, numericOnly.length <= 2 ? 2 : (numericOnly.length <= 4 ? numericOnly.length + 1 : numericOnly.length + 2));
          }

          input.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    }

    // Handle delete key
    if (event.key === 'Delete') {
      // If cursor is right before a slash, delete the slash and the character after it
      if (cursorPosition < value.length && value[cursorPosition] === '/') {
        event.preventDefault();

        // Remove the slash and the next character, then reformat
        const newValue = value.substring(0, cursorPosition) + value.substring(cursorPosition + 1);
        input.value = newValue;

        // Trigger input event to reformat
        input.dispatchEvent(new Event('input', { bubbles: true }));

        // Maintain cursor position
        setTimeout(() => {
          input.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
      }
    }
  }

  formatDate(event) {
    const input = event.target;
    const cursorPosition = input.selectionStart;
    const oldValue = input.value;
    let value = oldValue;

    // Remove all non-numeric characters
    value = value.replace(/\D/g, '');

    // Apply MM/DD/YYYY formatting
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }

    // Limit to 10 characters (MM/DD/YYYY)
    value = value.substring(0, 10);

    // Only update if value changed
    if (value !== oldValue) {
      // Update the input value
      input.value = value;

      // Calculate new cursor position
      // If a slash was just added, move cursor past it
      let newCursorPosition = cursorPosition;
      if (value.length > oldValue.length) {
        // Character was added
        if (value[cursorPosition] === '/') {
          newCursorPosition = cursorPosition + 1;
        }
      }

      // Set cursor position
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }

    // Trigger a custom event for form validation controllers
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
